import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import "mapbox-gl/dist/mapbox-gl.css";
import CustomDropdown from "../components/Dropdown.js";
import distributionMapIcon from "../assets/icons/analytics.png";
import digitalIcon from "../assets/icons/digital_twin.png";
import defectsNavIcon from "../assets/icons/select_defect_list.svg";
import customMarkerIcon from "../assets/icons/navigation.png";
import mapboxgl from "mapbox-gl";
import { AssetContext } from "../contexts/AssetListContext.js";
import BackButton from "../components/BackButton.js";
import WorkspaceYearSelect from "../components/WorkspaceYearSelect.js";
import Config from "../config";

const MAPBOX_TOKEN =
    "pk.eyJ1Ijoibmlrb2FuZHJlIiwiYSI6ImNtODU3MXJqcjAwcGkya3B1N3F6bzBiamoifQ.z8RU8-lQD7c0EBUGdfM_0g";
const MAP_MAX_ZOOM = 24;
const MAP_MIN_ZOOM = 0;

const getValidCoordinates = (points = []) =>
    points
        .filter(
            (point) =>
                Number.isFinite(point?.lon) && Number.isFinite(point?.lat),
        )
        .map((point) => [point.lon, point.lat]);

const customPopupStyle = `
  .mapboxgl-popup-content {
    font-family: 'Prometo Trial',
    background-color: #EBEBEB;
    color: #333333;
    padding: 12px;
    width: 179px;
    height: 66px;
    border-radius: 4px;
  }
  .mapboxgl-popup-tip {
    display: none;
  }
`;

/**
 * Home / landing: asset location map + entry points (Digital Twin, Distribution, Defects).
 */
const LandingPage = () => {
    const {
        assets,
        selectedYear,
        setSelectedYear,
        availableYears = [],
        isDefectsLoading = false,
    } = useContext(AssetContext);
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const popupRef = useRef(
        new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: "custom-popup",
            offset: 15,
        }),
    );
    const [, setSelectedPoint] = useState(null);

    const fitMapToAssetBounds = (coordinates) => {
        if (!mapRef.current || coordinates.length === 0) return;

        const bounds = coordinates.reduce(
            (nextBounds, coordinate) => nextBounds.extend(coordinate),
            new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
        );

        mapRef.current.fitBounds(bounds, {
            padding: 100,
            maxZoom: MAP_MAX_ZOOM,
            duration: 1000,
        });
    };

    useEffect(() => {
        const styleElement = document.createElement("style");
        styleElement.textContent = customPopupStyle;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    useEffect(() => {
        const validCoordinates = getValidCoordinates(assets);
        if (validCoordinates.length > 0 && !mapRef.current) {
            mapboxgl.accessToken = MAPBOX_TOKEN;
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: "mapbox://styles/mapbox/satellite-streets-v11",
                center: validCoordinates[0],
                zoom: 22,
                minZoom: MAP_MIN_ZOOM,
                maxZoom: MAP_MAX_ZOOM,
                attributionControl: false,
                dragRotate: false,
            });

            mapRef.current.on("load", () => {
                mapRef.current.loadImage(customMarkerIcon, (error, image) => {
                    if (error) throw error;

                    mapRef.current.addImage("custom-marker", image);

                    if (!mapRef.current.getSource("points")) {
                        mapRef.current.addSource("points", {
                            type: "geojson",
                            data: {
                                type: "FeatureCollection",
                                features: assets.map((asset) => ({
                                    type: "Feature",
                                    geometry: {
                                        type: "Point",
                                        coordinates: [asset.lon, asset.lat],
                                    },
                                    properties: {
                                        asset_name: asset.asset_name,
                                        asset_id: asset.asset_id,
                                    },
                                })),
                            },
                            cluster: false,
                        });

                        mapRef.current.addLayer({
                            id: "unclustered-point",
                            type: "symbol",
                            source: "points",
                            filter: ["!", ["has", "point_count"]],
                            layout: {
                                "icon-image": "custom-marker",
                                "text-field": ["get", "asset_id"],
                                "text-font": [
                                    "Open Sans Semibold",
                                    "Arial Unicode MS Bold",
                                ],
                                "text-offset": [0, 1.25],
                                "text-anchor": "top",
                                "icon-size": 0.05,
                            },
                            paint: {
                                "text-halo-color": "#FFFFFF",
                                "text-halo-width": 2,
                                "text-halo-blur": 0.5,
                            },
                        });

                        mapRef.current.on("click", "unclustered-point", (e) => {
                            const { asset_id } = e.features[0].properties;
                            navigate(`/asset-details/${asset_id}`);
                        });

                        mapRef.current.on(
                            "mouseenter",
                            "unclustered-point",
                            (e) => {
                                mapRef.current.getCanvas().style.cursor =
                                    "pointer";
                                const coordinates =
                                    e.features[0].geometry.coordinates.slice();
                                const { asset_name, asset_id } =
                                    e.features[0].properties;

                                while (
                                    Math.abs(e.lngLat.lng - coordinates[0]) >
                                    180
                                ) {
                                    coordinates[0] +=
                                        e.lngLat.lng > coordinates[0]
                                            ? 360
                                            : -360;
                                }

                                const popupContent = `
                <p style="margin: 0; color: #242424"><strong>ID:</strong> ${asset_id}</p>
                <p style="margin: 5px 0 0; color: #242424""><strong>Name:</strong> ${asset_name}</p>
              `;

                                popupRef.current
                                    .setLngLat(coordinates)
                                    .setHTML(popupContent)
                                    .addTo(mapRef.current);
                            },
                        );

                        mapRef.current.on(
                            "mouseleave",
                            "unclustered-point",
                            () => {
                                mapRef.current.getCanvas().style.cursor = "";
                                popupRef.current.remove();
                            },
                        );
                    }
                });
            });
            fitMapToAssetBounds(validCoordinates);
        }
    }, [assets, navigate]);

    useEffect(() => {
        const validCoordinates = getValidCoordinates(assets);
        if (!mapRef.current || validCoordinates.length === 0) return;

        fitMapToAssetBounds(validCoordinates);
    }, [assets]);

    return (
        <div className="map-container">
            <div className="landing-container">
                <div className="first-group">
                    <h1>{Config.title}</h1>
                    <h6>
                        Choose an asset to check the risk management details.
                    </h6>
                    <CustomDropdown
                        points={assets}
                        setSelectedPoint={setSelectedPoint}
                    />
                </div>
                <div className="second-group">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#EBEBEB",
                                borderRadius: "10px",
                                padding: "6px 10px",
                            }}
                        >
                            <WorkspaceYearSelect
                                id="landing-year-select"
                                selectedYear={selectedYear}
                                onYearChange={setSelectedYear}
                                availableYears={availableYears}
                                disabled={
                                    availableYears.length === 0 ||
                                    isDefectsLoading
                                }
                            />
                        </div>
                        <BackButton className="map-back-button" />
                    </div>
                    <Link to="/digital-twin" className="link-button">
                        <img
                            src={digitalIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Digital Twin</h6>
                            <span className="button-text">Digital Asset</span>
                        </div>
                    </Link>
                    <Link to="/distribution" className="link-button">
                        <img
                            src={distributionMapIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Distribution</h6>
                            <span className="button-text">
                                Defects on the map
                            </span>
                        </div>
                    </Link>
                    <Link to="/defects" className="link-button">
                        <img
                            src={defectsNavIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Defects</h6>
                            <span className="button-text">Defect table</span>
                        </div>
                    </Link>
                </div>
            </div>
            <div
                ref={mapContainerRef}
                style={{ height: "100%", width: "100%" }}
            />
        </div>
    );
};

export default LandingPage;
